import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

type ColumnTable = { [uid: string]: any };

const defaultColumnSettings = {
  type: "COLUMN_CATALOG_SETTINGS",
  columnTable: {},
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_107: DocMigration = {
  maxSchemaVersion: "0.0.107",
  description:
    "Rename column, profile, railing settings to include catalog. Consolidate default builder columns into first settings set.",
  apply(json: any) {
    // 1. Create a first settings set if it doesn't exist. Reference first settings set id.
    let firstSettingsSetPreExistedThisMigration = false;
    let firstSettingsSetId: string | undefined = undefined;
    for (const settingsSetId in json.settingsSets) {
      const settingsSet = json.settingsSets[settingsSetId];
      if (settingsSet.sortIndex !== 0) {
        continue;
      }
      firstSettingsSetId = settingsSet.id;
      firstSettingsSetPreExistedThisMigration = true;
    }

    if (firstSettingsSetId === undefined) {
      const baseSettingsSetId = uid();
      (json.settingsSets as any) = {};
      (json.settingsSets[baseSettingsSetId] as any) = {
        entityType: "SETTINGS_SET",
        name: "Base Settings Set",
        id: baseSettingsSetId,
        sortIndex: 0,
      };
      firstSettingsSetId = baseSettingsSetId;
    }

    let defaultPorchColumnId: string | undefined = "gl4942K5_ba2";
    let defaultRearPorchColumnId: string | undefined = "pl4942K5_ba4";

    const design = json.design;
    if (design) {
      const elevationStyleKey = design["elevationStyle"] as string;
      if (!elevationStyleKey) {
        const keys = Object.keys(design);
        let elevationKey: string | undefined = undefined;
        for (const key of keys) {
          if (key?.length > 8 && key.slice(0, 9).toLowerCase() === "elevation") {
            elevationKey = key;
            break;
          }
        }
        const builderCode: string | undefined = elevationKey ? (design[elevationKey] as string) : undefined;
        if (builderCode) {
          const columnIds = BuilderPorchColumnIds[builderCode];
          if (columnIds) {
            defaultPorchColumnId = columnIds.porchColumnId;
            defaultRearPorchColumnId = columnIds.rearPorchColumnId;
          }
        }
      } else if (elevationStyleKey) {
        const elevation = json.elevations[elevationStyleKey];
        if (elevation) {
          const baseSettings = elevation.baseSettings;
          if (baseSettings) {
            const columnIds = BuilderPorchColumnIds[baseSettings];
            if (columnIds) {
              defaultPorchColumnId = columnIds.porchColumnId;
              defaultRearPorchColumnId = columnIds.rearPorchColumnId;
            }
          }
        }
      }
    }

    let firstSettingsSetColumnSettingsId: string | undefined = undefined;
    let firstSettingsSetIncludesFrontPorchSettingsWithColumnId = false;
    let firstSettingsSetIncludesRearPorchSettingsWithColumnId = false;
    const uniqueColumnIds: { [id: string]: boolean } = {};

    // 2. Change Names to Include Catalog and Add any references to builder columns to list for first settings set
    for (const settingsId in json.settings) {
      const settings = json.settings[settingsId];
      if ((settings.type as any) === "COLUMN_SETTINGS") {
        (settings.type as any) = "COLUMN_CATALOG_SETTINGS";

        if (firstSettingsSetId !== undefined && settings.parentId !== undefined) {
          if (settings.parentId === firstSettingsSetId) {
            firstSettingsSetColumnSettingsId = settingsId;
          }
        }
      } else if ((settings.type as any) === "PROFILE_SETTINGS") {
        (settings.type as any) = "PROFILE_CATALOG_SETTINGS";
      } else if ((settings.type as any) === "RAILING_SETTINGS") {
        (settings.type as any) = "RAILING_CATALOG_SETTINGS";
      } else if (settings.type === "PORCH_SETTINGS") {
        if (settings.columnId) {
          const result = columnMigrationTable[settings.columnId];
          if (result) {
            uniqueColumnIds[settings.columnId] = true;
          }
          if (firstSettingsSetPreExistedThisMigration && settings.parentId === firstSettingsSetId) {
            firstSettingsSetIncludesFrontPorchSettingsWithColumnId = true;
          }
        }
      } else if (settings.type === "REAR_PORCH_SETTINGS") {
        if (settings.columnId) {
          const result = columnMigrationTable[settings.columnId];
          if (result) {
            uniqueColumnIds[settings.columnId] = true;
          }
          if (firstSettingsSetPreExistedThisMigration && settings.parentId === firstSettingsSetId) {
            firstSettingsSetIncludesRearPorchSettingsWithColumnId = true;
          }
        }
      }
    }

    if (defaultPorchColumnId) {
      uniqueColumnIds[defaultPorchColumnId] = true;
    }

    if (defaultRearPorchColumnId) {
      uniqueColumnIds[defaultRearPorchColumnId] = true;
    }

    // 3. Add builder columns directly to first settings set
    if (firstSettingsSetColumnSettingsId === undefined) {
      // create new settings
      const newSettingsId = uid();
      const defaultSettings = { ...defaultColumnSettings };

      for (const uniqueColumnId in uniqueColumnIds) {
        defaultSettings.columnTable[uniqueColumnId] = columnMigrationTable[uniqueColumnId];
      }

      (json.settings[newSettingsId] as any) = {
        ...defaultSettings,
        entityType: "SETTINGS",
        id: newSettingsId,
        parentId: firstSettingsSetId,
        sortIndex: 0,
      };
    } else {
      // add columns to first settings set
      const settings = json.settings[firstSettingsSetColumnSettingsId];

      if (!settings.columnTable) {
        (settings.columnTable as any) = {};
      }

      for (const uniqueColumnId in uniqueColumnIds) {
        if (!settings.columnTable[uniqueColumnId]) {
          settings.columnTable[uniqueColumnId] = columnMigrationTable[uniqueColumnId];
        }
      }
    }

    let porchColumnIdSet = false;
    let rearPorchColumnIdSet = false;

    for (const settingsSetId in json.settingsSets) {
      const settingsSet = json.settingsSets[settingsSetId];
      if (settingsSet.name !== "Base Settings Set") {
        continue;
      }

      if (defaultPorchColumnId === undefined && defaultRearPorchColumnId === undefined) {
        continue;
      }

      const { porchColumnIdSet: a, rearPorchColumnIdSet: b } = updateOrAddColumnIdToPorchSettings(
        json,
        settingsSetId,
        defaultPorchColumnId,
        defaultRearPorchColumnId,
        true,
        undefined,
        undefined,
        !firstSettingsSetIncludesFrontPorchSettingsWithColumnId,
        !firstSettingsSetIncludesRearPorchSettingsWithColumnId,
      );
      porchColumnIdSet = a;
      rearPorchColumnIdSet = b;
      break;
    }

    //5. find elevation-based settings sets created in "migration_0_0_95" and add porch settings with column references if necessary
    for (const elevationId in json.elevations) {
      const elevation = json.elevations[elevationId];
      const prettyName = elevation.prettyName;
      const baseSettings = elevation.baseSettings;
      if (baseSettings && prettyName?.length > 0) {
        const columnIds = BuilderPorchColumnIds[baseSettings];
        if (columnIds) {
          defaultPorchColumnId = columnIds.porchColumnId;
          defaultRearPorchColumnId = columnIds.rearPorchColumnId;

          if (defaultPorchColumnId === undefined && defaultRearPorchColumnId === undefined) {
            continue;
          }

          if (
            firstSettingsSetIncludesFrontPorchSettingsWithColumnId &&
            firstSettingsSetIncludesRearPorchSettingsWithColumnId
          ) {
            continue;
          }

          let parentSettingsSetId: string | undefined;
          for (const settingsSetId in json.settingsSets) {
            const settingsSet = json.settingsSets[settingsSetId];
            if (settingsSet.name === "Settings Set for " + prettyName) {
              parentSettingsSetId = settingsSet.id;
              break;
            }
          }

          if (parentSettingsSetId === undefined) {
            continue;
          }

          updateOrAddColumnIdToPorchSettings(
            json,
            parentSettingsSetId,
            defaultPorchColumnId,
            defaultRearPorchColumnId,
            false,
            porchColumnIdSet,
            rearPorchColumnIdSet,
            !firstSettingsSetIncludesFrontPorchSettingsWithColumnId,
            !firstSettingsSetIncludesRearPorchSettingsWithColumnId,
          );
        }
      }
    }

    return json;
  },
};

export default migration_0_0_107;

function updateOrAddColumnIdToPorchSettings(
  json: any,
  parentSettingsSetId: string,
  defaultPorchColumnId: string | undefined,
  defaultRearPorchColumnId: string | undefined,
  workingOnBaseSettings: boolean,
  basePorchColumnIdSet?: boolean,
  baseRearPorchColumnIdSet?: boolean,
  makeFrontPorchSettings = true,
  makeRearPorchSettings = true,
): {
  porchColumnIdSet: boolean;
  rearPorchColumnIdSet: boolean;
} {
  let porchColumnIdSet = false;
  let rearPorchColumnIdSet = false;
  let foundPorch = false;
  let foundRearPorch = false;

  if (!workingOnBaseSettings) {
    //preflight check around rear porches
    let preflightFrontPorchColumnIdSet = false;
    let preflightRearPorchColumnIdSet = false;
    let parentSettingsFound = false;
    for (const settingsId in json.settings) {
      const settings = json.settings[settingsId];
      if (settings.type === "REAR_PORCH_SETTINGS" && settings.parentId === parentSettingsSetId) {
        preflightRearPorchColumnIdSet = true;
        parentSettingsFound = true;
      }
      if (settings.type === "PORCH_SETTINGS" && settings.parentId === parentSettingsSetId) {
        preflightFrontPorchColumnIdSet = true;
        parentSettingsFound = true;
      }
    }

    if (parentSettingsFound && preflightRearPorchColumnIdSet !== preflightFrontPorchColumnIdSet) {
      return { porchColumnIdSet, rearPorchColumnIdSet };
    }
  }

  for (const settingsId in json.settings) {
    const settings = json.settings[settingsId];

    if (
      !basePorchColumnIdSet &&
      settings.type === "PORCH_SETTINGS" &&
      settings.parentId &&
      settings.parentId === parentSettingsSetId &&
      makeFrontPorchSettings
    ) {
      if (settings.columnId !== undefined) {
        porchColumnIdSet = true;
      }

      if (defaultPorchColumnId !== undefined && settings.columnId === undefined) {
        settings.columnId = defaultPorchColumnId;
      }

      foundPorch = true;
    } else if (
      !baseRearPorchColumnIdSet &&
      settings.type === "REAR_PORCH_SETTINGS" &&
      settings.parentId &&
      settings.parentId === parentSettingsSetId &&
      makeRearPorchSettings
    ) {
      if (settings.columnId !== undefined) {
        rearPorchColumnIdSet = true;
      }
      if (defaultPorchColumnId !== undefined && settings.columnId === undefined) {
        settings.columnId = defaultRearPorchColumnId;
      }
      foundRearPorch = true;
    }
    if (foundPorch && foundRearPorch) {
      break;
    }
  }

  if (!foundPorch && defaultPorchColumnId !== undefined && makeFrontPorchSettings) {
    const newSettingsId = uid();

    (json.settings[newSettingsId] as any) = {
      type: "PORCH_SETTINGS",
      entityType: "SETTINGS",
      id: newSettingsId,
      parentId: parentSettingsSetId,
      sortIndex: 0,
      columnId: defaultPorchColumnId,
    };
  }

  if (!foundRearPorch && defaultRearPorchColumnId !== undefined && makeRearPorchSettings) {
    const newSettingsId = uid();

    (json.settings[newSettingsId] as any) = {
      type: "REAR_PORCH_SETTINGS",
      entityType: "SETTINGS",
      id: newSettingsId,
      parentId: parentSettingsSetId,
      sortIndex: 0,
      columnId: defaultRearPorchColumnId,
    };
  }

  return { porchColumnIdSet, rearPorchColumnIdSet };
}

const columnMigrationTable: ColumnTable = {
  ["DEFAULT"]: {
    name: `3" Column`,
    entityType: "COLUMN",
    sections: [{ height: 0, width: 3, paletteId: "trimExterior0", taper: false }],
  },
  ["y2k_33K5Qsh1"]: {
    name: "RT Default", //"RedTree Default"
    entityType: "COLUMN",
    sections: [
      { height: 4, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 4, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["g3e_q2K5Q_h1"]: {
    name: "SD Front", //Smith Douglas
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 2, width: 7, paletteId: "trimExterior0" },
    ],
  },
  ["56epq_KYQgi7"]: {
    name: "SD Rear", //Smith Douglas
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 2, width: 7, paletteId: "trimExterior0" },
    ],
  },
  ["aatpy2_89sz2"]: {
    name: "ES Default", // Essex
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["ggtpy2_89sz2"]: {
    name: "ES Default", // Esperanza
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 2, width: 7, paletteId: "trimExterior0" },
    ],
  },
  ["fftpy2_89sz2"]: {
    name: "EP Default", // Epcon
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 2, width: 7, paletteId: "trimExterior0" },
    ],
  },
  ["Kl4942K5_ba3"]: {
    name: "EL French Country", // Elliott
    entityType: "COLUMN",
    sections: [
      { height: 34, width: 8, paletteId: "masonry0" },
      { height: 1.5, width: 8 + 1.5, paletteId: "masonryTrim0:Rowlock" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["gl4942K5_ba2"]: {
    name: "El Standard", // Elliott
    entityType: "COLUMN",
    sections: [
      { height: 8, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["pl4942K5_ba4"]: {
    name: "El Masonry", // Elliott
    entityType: "COLUMN",
    sections: [
      { height: 8, width: 8, paletteId: "masonry0" },
      { height: 0, width: 7, paletteId: "masonry0" },
      { height: 6, width: 8, paletteId: "masonry0" },
    ],
  },
  ["zye_7698ysz2"]: {
    name: "C Default 8", // Craft
    entityType: "COLUMN",
    sections: [{ height: 0, width: 8, paletteId: "wallExterior0" }],
  },
  ["56e_u7o8y473"]: {
    name: "C Default .1", // Craft
    entityType: "COLUMN",
    sections: [{ height: 0.01, width: 0.1, paletteId: "wallExterior0" }],
  },
  ["g3e_y2K8_sz2"]: {
    name: "C Default", // Brock
    entityType: "COLUMN",
    sections: [
      { height: 1, width: 8, paletteId: "trimExterior0" },
      { height: 6, width: 7, paletteId: "trimExterior0" },
      { height: 0, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["g3e_q2K5Qsh1"]: {
    name: "B Masonry", // Beazer
    entityType: "COLUMN",
    sections: [
      { height: 8, width: 6, paletteId: "masonry0" },
      { height: 0, width: 5, paletteId: "masonry0" },
      { height: 6, width: 6, paletteId: "masonry0" },
    ],
  },
  ["65e_q2KYQgh2"]: {
    name: "B Masonry Trim", // Beazer
    entityType: "COLUMN",
    sections: [
      { height: 34, width: 8, paletteId: "masonry0" },
      { height: 1.5, width: 8 + 1.5, paletteId: "masonryTrim0:Rowlock" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
    ],
  },
  ["6w5e_q2SYGgh6"]: {
    name: "B Trim", // Beazer
    entityType: "COLUMN",
    sections: [
      { height: 6, width: 6, paletteId: "trimExterior0" },
      { height: 0, width: 5, paletteId: "trimExterior0" },
      { height: 6, width: 6, paletteId: "trimExterior0" },
    ],
  },
};

interface PorchColumnIds {
  readonly porchColumnId: string | undefined;
  readonly rearPorchColumnId: string | undefined;
}

type BuilderColumnIdTable = { [key: string]: PorchColumnIds };

const BuilderPorchColumnIds: BuilderColumnIdTable = {
  ["Default"]: { porchColumnId: "DEFAULT", rearPorchColumnId: "DEFAULT" },

  ["Beazer_A2_TRM"]: { porchColumnId: "6w5e_q2SYGgh6", rearPorchColumnId: "g3e_q2K5Qsh1" },
  ["Beazer_A2_ACL"]: { porchColumnId: "65e_q2KYQgh2", rearPorchColumnId: "g3e_q2K5Qsh1" },
  ["Beazer_A2_FCL"]: { porchColumnId: "6w5e_q2SYGgh6", rearPorchColumnId: "g3e_q2K5Qsh1" },

  ["Brock_L20"]: { porchColumnId: "g3e_y2K8_sz2", rearPorchColumnId: undefined },
  ["Brock_L30"]: { porchColumnId: "g3e_y2K8_sz2", rearPorchColumnId: undefined },
  ["Brock_L31"]: { porchColumnId: "g3e_y2K8_sz2", rearPorchColumnId: undefined },

  ["Craft_M2C"]: { porchColumnId: "zye_7698ysz2", rearPorchColumnId: "zye_7698ysz2" },
  ["Craft_M2M"]: { porchColumnId: "56e_u7o8y473", rearPorchColumnId: "zye_7698ysz2" },
  ["Craft_M2P"]: { porchColumnId: "zye_7698ysz2", rearPorchColumnId: "zye_7698ysz2" },

  ["Elliott_H00"]: { porchColumnId: "gl4942K5_ba2", rearPorchColumnId: "pl4942K5_ba4" },
  ["Elliott_H10"]: { porchColumnId: "gl4942K5_ba2", rearPorchColumnId: "pl4942K5_ba4" },
  ["Elliott_H20"]: { porchColumnId: "Kl4942K5_ba3", rearPorchColumnId: "pl4942K5_ba4" },
  ["Elliott_J00"]: { porchColumnId: "gl4942K5_ba2", rearPorchColumnId: "pl4942K5_ba4" },
  ["Elliott_F00"]: { porchColumnId: "gl4942K5_ba2", rearPorchColumnId: "pl4942K5_ba4" },

  ["Epcon_P2B"]: { porchColumnId: "fftpy2_89sz2", rearPorchColumnId: undefined },
  ["Epcon_P2C"]: { porchColumnId: "fftpy2_89sz2", rearPorchColumnId: undefined },
  ["Epcon_P2D"]: { porchColumnId: "fftpy2_89sz2", rearPorchColumnId: undefined },

  ["Esperanza_F2T"]: { porchColumnId: "ggtpy2_89sz2", rearPorchColumnId: undefined },
  ["Esperanza_F2F"]: { porchColumnId: "ggtpy2_89sz2", rearPorchColumnId: undefined },
  ["Esperanza_F2A"]: { porchColumnId: "ggtpy2_89sz2", rearPorchColumnId: undefined },

  ["Essex_W1A"]: { porchColumnId: "aatpy2_89sz2", rearPorchColumnId: undefined },
  ["Essex_W2A"]: { porchColumnId: "aatpy2_89sz2", rearPorchColumnId: undefined },
  ["Essex_W2D"]: { porchColumnId: "aatpy2_89sz2", rearPorchColumnId: undefined },

  ["PaletteMF01"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF02"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF03"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF04"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF05"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF06"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF07"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF08"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF09"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF10"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF11"]: { porchColumnId: undefined, rearPorchColumnId: undefined },
  ["PaletteMF12"]: { porchColumnId: undefined, rearPorchColumnId: undefined },

  ["RedTree_001"]: { porchColumnId: "y2k_33K5Qsh1", rearPorchColumnId: undefined },
  ["RedTree_002"]: { porchColumnId: "y2k_33K5Qsh1", rearPorchColumnId: undefined },
  ["RedTree_003"]: { porchColumnId: "y2k_33K5Qsh1", rearPorchColumnId: undefined },

  ["SmithDouglas_B1C"]: { porchColumnId: "g3e_q2K5Q_h1", rearPorchColumnId: "56epq_KYQgi7" },
  ["SmithDouglas_B1E"]: { porchColumnId: "g3e_q2K5Q_h1", rearPorchColumnId: "56epq_KYQgi7" },
  ["SmithDouglas_B2J"]: { porchColumnId: "g3e_q2K5Q_h1", rearPorchColumnId: "56epq_KYQgi7" },
  ["SmithDouglas_B1G"]: { porchColumnId: "g3e_q2K5Q_h1", rearPorchColumnId: "56epq_KYQgi7" },
};
